import React, { useEffect, useState } from "react";
import Logo from "../../Images/dsmlogoblack.png";
import "../../Styles/ChangePassword.css"; // reuse existing styles
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  sendForgotPassword,
  resetForgotPassword,
} from "../../Redux/Auth/ForgotPasswordAction";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RESET_LINK_COOLDOWN_MS = 2 * 60 * 1000;
const RESET_LINK_COOLDOWN_KEY = "forgotPasswordResetLinkCooldownUntil";
const RESET_LINK_COOLDOWN_MESSAGE =
  "Reset link is sent. Please try again in 2 mins.";

const ForgotPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const { loading, sent, error } = useSelector((s) => s.ForgotPassword || {});

  const [localErr, setLocalErr] = useState("");
  const [cooldownMessage, setCooldownMessage] = useState("");
  const [, setCooldownStartedAt] = useState(0);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [openError, setOpenError] = useState(false);
  const [showSentModal, setShowSentModal] = useState(false);
  const isCooldownActive = cooldownUntil > Date.now();

  useEffect(() => {
    const storedCooldown = localStorage.getItem(RESET_LINK_COOLDOWN_KEY);
    let storedCooldownStartedAt = 0;
    let storedCooldownUntil = 0;

    try {
      const parsedCooldown = JSON.parse(storedCooldown);
      storedCooldownStartedAt = Number(parsedCooldown?.startedAt);
      storedCooldownUntil = Number(parsedCooldown?.endsAt);
    } catch (_) {
      storedCooldownUntil = Number(storedCooldown);
    }

    if (storedCooldownUntil && storedCooldownUntil > Date.now()) {
      setCooldownStartedAt(storedCooldownStartedAt || Date.now());
      setCooldownUntil(storedCooldownUntil);
    } else {
      localStorage.removeItem(RESET_LINK_COOLDOWN_KEY);
    }
  }, []);

  useEffect(() => {
    if (!cooldownUntil) return;

    const remaining = cooldownUntil - Date.now();
    if (remaining <= 0) {
      setCooldownStartedAt(0);
      setCooldownUntil(0);
      setCooldownMessage("");
      localStorage.removeItem(RESET_LINK_COOLDOWN_KEY);
      return;
    }

    const timer = setTimeout(() => {
      setCooldownStartedAt(0);
      setCooldownUntil(0);
      setCooldownMessage("");
      localStorage.removeItem(RESET_LINK_COOLDOWN_KEY);
    }, remaining);

    return () => clearTimeout(timer);
  }, [cooldownUntil]);

  // cleanup on unmount
  useEffect(
    () => () => {
      dispatch(resetForgotPassword());
    },
    [dispatch]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    setLocalErr("");

    if (isCooldownActive) {
      setCooldownMessage(RESET_LINK_COOLDOWN_MESSAGE);
      return;
    }

    const trimmed = email.trim();
    if (!trimmed) {
      setLocalErr("Please enter your email.");
      return;
    }
    if (!EMAIL_RE.test(trimmed)) {
      setLocalErr("Enter a valid email address.");
      return;
    }

    dispatch(sendForgotPassword(trimmed));
  };

  const handleSubmitButtonClick = () => {
    if (isCooldownActive) {
      setCooldownMessage(RESET_LINK_COOLDOWN_MESSAGE);
    }
  };

  const handleCloseModal = () => {
    setShowSentModal(false);
    setOpenError(false);
    dispatch(resetForgotPassword());
    navigate("/");
  };

  const handleUseDifferentEmail = () => {
    setEmail("");
    setShowSentModal(false);
    setOpenError(false);
    dispatch(resetForgotPassword());
  };

  // Outlook opener (used from modal)
  const openOutlookAppStrict = () => {
    setOpenError(false);
    const ua = navigator.userAgent || navigator.vendor || "";

    let watchdog = setTimeout(() => setOpenError(true), 1500);

    const tryHref = (url) => {
      try {
        window.location.href = url;
      } catch (_) { }
    };

    // iOS
    if (/iPad|iPhone|iPod/i.test(ua)) {
      tryHref("ms-outlook://");
      return;
    }

    // Android
    if (/Android/i.test(ua)) {
      tryHref(
        "intent://mail/#Intent;scheme=ms-outlook;package=com.microsoft.office.outlook;end"
      );
      return;
    }

    // Desktop
    const candidates = ["ms-outlook://", "outlook://", "microsoft-outlook://"];

    let i = 0;
    const tick = () => {
      if (i >= candidates.length) return;
      tryHref(candidates[i++]);
      setTimeout(tick, 300);
    };
    tick();

    const clear = () => {
      clearTimeout(watchdog);
      document.removeEventListener("visibilitychange", clear);
    };
    document.addEventListener("visibilitychange", clear);
  };

  // When backend says "sent", show our custom modal
  useEffect(() => {
    if (sent && !error) {
      const nextCooldownStartedAt = Date.now();
      const nextCooldownUntil = nextCooldownStartedAt + RESET_LINK_COOLDOWN_MS;
      setCooldownStartedAt(nextCooldownStartedAt);
      setCooldownUntil(nextCooldownUntil);
      localStorage.setItem(
        RESET_LINK_COOLDOWN_KEY,
        JSON.stringify({
          startedAt: nextCooldownStartedAt,
          endsAt: nextCooldownUntil,
        })
      );
      setCooldownMessage("");
      setShowSentModal(true);
    }
  }, [sent, error]);

  return (
    <section className="login-section-bg">
      <div className="container">
        <br />
        <br />
        <center>
          <div className="login-section">
            <center>
              <img src={Logo} alt="Demo Central Logo" width="250" />

              {/* Always show the form. Success is handled via modal */}
              <div className="log-sec-2">
                <h3 className="log-sec-sign-in">Forgot Password</h3>
                <p className="log-sec-sign-in-sub">
                  Enter your registered email. We’ll send you a reset link.
                </p>

                <form onSubmit={handleSubmit} style={{ width: "100%" }}>
                  <input
                    className="input-box-username"
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    aria-invalid={!!localErr}
                    aria-describedby={localErr ? "fp-error" : undefined}
                  />
                  {localErr && (
                    <div
                      id="fp-error"
                      style={{
                        color: "#d9534f",
                        fontSize: 13,
                        marginTop: 6,
                        marginBottom: -6,
                      }}
                    >
                      {localErr}
                    </div>
                  )}
                  <br />

                  <button
                    type="submit"
                    className={`login-btn${isCooldownActive ? " fp-cooldown-btn" : ""}`}
                    disabled={loading}
                    aria-disabled={isCooldownActive}
                    aria-busy={loading}
                    onClick={handleSubmitButtonClick}
                  >
                    {loading ? "Sending..." : "Send Reset Link"}
                  </button>

                  {cooldownMessage && (
                    <div className="fp-cooldown-message" role="alert">
                      {cooldownMessage}
                    </div>
                  )}

                  <p
                    className="reset-pass"
                    onClick={() => navigate("/")}
                    style={{ marginTop: 12 }}
                  >
                    Back to Sign In
                  </p>
                </form>

                {/* Server error (generic, no email enumeration) */}
                {error && (
                  <p
                    style={{
                      marginTop: 10,
                      fontSize: 13,
                      color: "#d9534f",
                    }}
                  >
                    {error}
                  </p>
                )}
              </div>
            </center>
          </div>
        </center>
      </div>

      {showSentModal && (
        <div
          className="fp-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="fp-modal-title"
        >
          <div
            className="fp-modal fp-modal-enter"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fp-modal-icon">
              <svg
                width="52"
                height="52"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <path
                  d="M7 12.5l3.2 3.2L17.5 8.5"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <h3 id="fp-modal-title" className="fp-modal-title">
              Reset link sent
            </h3>

            <p className="fp-modal-text">
              If an account exists for <strong>{email}</strong>, you’ll receive an
              email with a password reset link. Please check your Outlook inbox and
              spam folder.
            </p>

            <div className="fp-modal-actions">
              <button
                className="fp-modal-primary-btn"
                type="button"
                onClick={openOutlookAppStrict}
              >
                Open Outlook
              </button>
              <button
                className="fp-modal-secondary-btn"
                type="button"
                onClick={handleCloseModal}
              >
                Close
              </button>
            </div>

            <button
              type="button"
              className="fp-modal-link-btn"
              onClick={handleUseDifferentEmail}
            >
              Use a different email
            </button>

            {openError && (
              <p className="fp-modal-hint">
                Couldn’t open Outlook automatically. Please open the Outlook app on
                your device.
              </p>
            )}
          </div>
        </div>
      )}

    </section>
  );
};

export default ForgotPassword;
